import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { BASE_URL } from "../config";

/**
 * Hook to manage tab locking using API endpoints
 * Only one tab can have the lock at a time, allowing it to perform actions
 * Other tabs will be in read-only mode
 * 
 * @param {string} taskId - Task ID for locking (e.g., 'backup')
 * @returns {object} - { isLocked: boolean, isActiveTab: boolean, lockedBy: string }
 */
const useApiTabLock = (taskId) => {
  const [isLocked, setIsLocked] = useState(false);
  const [isActiveTab, setIsActiveTab] = useState(false);
  const [lockedBy, setLockedBy] = useState(null);
  const location = useLocation();
  const checkIntervalRef = useRef(null);
  const isUnmountingRef = useRef(false);

  // Get user info from localStorage
  const getUserId = () => localStorage.getItem("user_id") || "";
  const getUserName = () => localStorage.getItem("user_name") || "";
  const getToken = () => {
    return localStorage.rememberMe === "true"
      ? localStorage.token
      : sessionStorage.token;
  };

  // Check lock status from API
  const checkLockStatus = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const userId = getUserId();
      const userName = getUserName();

      // Use GET method with query parameters
      const params = new URLSearchParams({
        task_id: taskId,
        user_id: userId,
        user_name: userName,
        action: "edit",
      });
      
      const response = await fetch(`${BASE_URL}tasks/${taskId}/lock-status?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // If API fails, assume no lock
        setIsLocked(false);
        setIsActiveTab(true);
        setLockedBy(null);
        return;
      }

      const data = await response.json();
      
      // Check if lock exists
      // Response structure: 
      // { lock: null } or 
      // { lock: { task_id, user_id, user_name, action, locked_at, last_ping_at } }
      if (!data || !data.lock || data.lock === null) {
        // No lock exists, we can acquire lock
        setIsLocked(false);
        setIsActiveTab(true);
        setLockedBy(null);
        return;
      }

      const currentUserId = getUserId();
      const lockUserId = data.lock.user_id;
      
      // Compare user IDs - normalize to strings for comparison to avoid type mismatch issues
      const normalizedCurrentUserId = String(currentUserId || "").trim();
      const normalizedLockUserId = String(lockUserId || "").trim();
      
      // Compare user IDs - if they don't match, disable actions
      if (normalizedCurrentUserId && normalizedLockUserId && normalizedCurrentUserId === normalizedLockUserId) {
        // We own the lock - allow editing
        setIsLocked(false);
        setIsActiveTab(true);
        setLockedBy(null);
      } else if (normalizedCurrentUserId && normalizedLockUserId) {
        // User ID doesn't match - someone else has the lock, disable actions
        console.warn("Lock user ID mismatch:", {
          currentUserId: normalizedCurrentUserId,
          lockUserId: normalizedLockUserId,
          match: normalizedCurrentUserId === normalizedLockUserId
        });
        setIsLocked(true);
        setIsActiveTab(false);
        setLockedBy(data.lock.user_name || "Another user");
      } else {
        // One or both user IDs are missing - assume we can edit (fail open)
        console.warn("Missing user IDs in lock check:", {
          currentUserId: normalizedCurrentUserId,
          lockUserId: normalizedLockUserId
        });
        setIsLocked(false);
        setIsActiveTab(true);
        setLockedBy(null);
      }
    } catch (error) {
      console.error("Error checking lock status:", error);
      // On error, assume we can edit (fail open)
      setIsLocked(false);
      setIsActiveTab(true);
      setLockedBy(null);
    }
  };

  // Unlock API call
  const unlock = async () => {
    try {
      const token = getToken();
      if (!token) return;

      await fetch(`${BASE_URL}tasks/unlock`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          task_id: taskId,
          user_id: getUserId(),
        }),
      });
    } catch (error) {
      console.error("Error unlocking:", error);
    }
  };

  useEffect(() => {
    // Check lock status immediately once
    checkLockStatus();

    // Handle beforeunload
    const handleBeforeUnload = () => {
      isUnmountingRef.current = true;
      // Note: unlock is handled by the component (e.g., on back button click)
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup
    return () => {
      isUnmountingRef.current = true;
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Note: unlock is handled by the component (e.g., on back button click)
    };
  }, [taskId]); // Only re-run if taskId changes

  return { isLocked, isActiveTab, lockedBy };
};

export default useApiTabLock;
